// eslint-disable-next-line import/no-cycle
import store from '@client/store';
import {Route, NavigationGuardNext} from 'vue-router';
import {onboardingGoingLiveBeforeServicing} from '@client/remote-config';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const onboardingLiveBeforeServicing = async () => {
  const onboardingBeforeServicing = await onboardingGoingLiveBeforeServicing();

  return onboardingBeforeServicing;
};

export enum TransfersRouteNames {
  Confirm = 'Confirm',
  Deposit = 'Deposit',
  EditDeposit = 'EditDeposit',
  EditWithdrawal = 'EditWithdrawal',
  Withdrawal = 'Withdrawal',
  Link = 'Link',
  Transactions = 'Transactions',
  TransactionConfirm = 'TransactionConfirm',
  TransactionDeposit = 'TransactionDeposit',
  TransactionEditDeposit = 'TransactionEditDeposit',
  TransactionEditWithdrawal = 'TransactionEditWithdrawal',
  TransactionWithdrawal = 'TransactionWithdrawal',
}

const routes = [
  {
    path: '/clientdashboard/transactions',
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    component: () => import('@client/layouts/authenticated/Index.vue'),
    meta: {
      useLayouts: true,
    },
    beforeEnter: async (_to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
      if (await onboardingLiveBeforeServicing()) {
        window.location.href =
          process.env.VUE_APP_ENV === 'prod'
            ? 'https://advisedservices.individuals.principal.com'
            : 'https://advisedservices.individuals.staging.principal.com';
      }

      // eslint-disable-next-line callback-return
      next();
    },
    beforeRouteLeave: (_to: Route, _from: Route, next: NavigationGuardNext): void => {
      store.commit('transfers/SET_CLEAR');
      next();
    },
    children: [
      {
        path: '',
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        component: () => import('@client/pages/transactions/TransactionsPage.vue'),
        name: TransfersRouteNames.Transactions,
        meta: {
          CMS: true,
          useLayouts: true,
        },
        beforeEnter: async (_to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
          await Promise.all([
            store.dispatch('transfers/getTransferRules'),
            store.dispatch('transfers/getTransferFrequencies'),
            store.dispatch('accountList/loadAccountList'),
            store.dispatch('transfers/hasCustodianAccounts'),
          ]);

          next();
        },
      },
      {
        path: 'confirm',
        name: TransfersRouteNames.TransactionConfirm,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        component: () => import('@client/components/transfers/Confirm.vue'),
        meta: {
          useLayouts: true,
        },
        beforeEnter: (_to: Route, from: Route, next: NavigationGuardNext): void => {
          const validReferrers = [
            TransfersRouteNames.TransactionDeposit.toString(),
            TransfersRouteNames.TransactionEditDeposit.toString(),
            TransfersRouteNames.TransactionEditWithdrawal.toString(),
            TransfersRouteNames.TransactionWithdrawal.toString(),
          ];

          if (!validReferrers.includes(from.name)) {
            store.dispatch('setLoading', false);
            next(false);

            return;
          }

          next();
        },
      },
      {
        path: 'edit/deposit/:transferId',
        name: TransfersRouteNames.TransactionEditDeposit,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        component: () => import('@client/components/transfers/Deposit.vue'),
        meta: {
          useLayouts: true,
        },
        beforeEnter: async (to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
          await Promise.all([
            store.commit('transfers/SET_TRANSFER_TYPE', 'Deposit'),
            store.dispatch('transfers/getCustodianAccounts'),
          ]);

          await Promise.all([
            store.dispatch('transfers/getTransferDetail', {transferId: to.params.transferId}),
            store.commit('transfers/SET_ADVISOR_INITIATED_ON_EDIT', to.query),
          ]);

          await store.dispatch('transfers/getBankAccounts', store.state.transfers.destinationId);

          next();
        },
      },
      {
        path: 'edit/withdrawal/:transferId',
        name: TransfersRouteNames.TransactionEditWithdrawal,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        component: () => import('@client/components/transfers/Withdrawal.vue'),
        meta: {
          useLayouts: true,
        },
        beforeEnter: async (_to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
          await Promise.all([
            store.commit('transfers/SET_TRANSFER_TYPE', 'Withdrawal'),
            store.dispatch('transfers/getCustodianAccounts'),
          ]);

          await Promise.all([
            store.dispatch('transfers/getTransferDetail', {transferId: _to.params.transferId}),
            store.commit('transfers/SET_ADVISOR_INITIATED_ON_EDIT', _to.query),
          ]);

          await store.dispatch('transfers/getBankAccounts', store.state.transfers.sourceId);

          next();
        },
      },
      {
        path: 'withdrawal',
        name: TransfersRouteNames.TransactionWithdrawal,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        component: () => import('@client/components/transfers/Withdrawal.vue'),
        props: true,
        meta: {
          useLayouts: true,
        },
        beforeEnter: async (_to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
          await Promise.all([
            store.commit('transfers/SET_TRANSFER_TYPE', 'Withdrawal'),
            store.dispatch('transfers/getTransferFrequencies'),
            store.dispatch('transfers/getTransferRules'),
            store.dispatch('accountList/loadAccountList'),
            store.dispatch('transfers/getCustodianAccounts'),
            // store.dispatch('transfers/getWithdrawalAmtTypes'),
          ]);

          next();
        },
      },
      {
        path: 'deposit',
        name: TransfersRouteNames.TransactionDeposit,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        component: () => import('@client/components/transfers/Deposit.vue'),
        props: true,
        meta: {
          useLayouts: true,
        },
        beforeEnter: async (_to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
          await Promise.all([
            store.commit('transfers/SET_TRANSFER_TYPE', 'Deposit'),
            store.dispatch('transfers/getTransferRules'),
            store.dispatch('transfers/getTransferFrequencies'),
            store.dispatch('accountList/loadAccountList'),
            store.dispatch('transfers/getCustodianAccounts'),
          ]);

          next();
        },
      },
    ],
  },
];

export default routes;
